import type { RequestOptions } from '@@/plugin-request/request';
import { request as umiRequest } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';

type ScopedRequestOptions = RequestOptions;

export interface ContractCenterResponse<T> {
  code: number;
  msg: string;
  data: T | null;
}

export class ContractCenterApiError extends Error {
  constructor(
    message: string,
    readonly code: number,
    readonly data: unknown = null,
  ) {
    super(message);
    this.name = 'ContractCenterApiError';
  }
}

const CONTRACT_CENTER_BFF_PREFIXES = [
  '/bff/contracts',
  '/bff/contracts/approval-tasks',
  '/bff/signing-parties',
  '/bff/contract-types',
  '/bff/number-rules',
  '/bff/dashboard',
  '/bff/analytics',
  '/bff/contract-events',
  '/bff/company-entities',
  '/bff/reviews',
  '/bff/settings',
];

const isContractCenterBffPath = (url: string) =>
  CONTRACT_CENTER_BFF_PREFIXES.some(
    (prefix) => url === prefix || url.startsWith(`${prefix}/`),
  );

export const createRequest = (baseURL?: string) => {
  const scopedBaseURL = baseURL ?? BizAppConfig.appApiUrl ?? '';
  return <T = unknown>(url: string, options?: ScopedRequestOptions) =>
    (
      umiRequest as unknown as <R = unknown>(
        requestUrl: string,
        requestOptions?: ScopedRequestOptions,
      ) => Promise<R>
    )<T>(url, {
      baseURL: scopedBaseURL,
      ...(options || {}),
    });
};

export const createAppRequest = () => createRequest(BizAppConfig.appApiUrl);
export const createTenantManagementRequest = () =>
  createRequest(BizAppConfig.appTenantManagementUrl);

export const createContractCenterRequest = () => {
  const request = createRequest(BizAppConfig.appApiUrl);
  return async <T = unknown>(
    url: string,
    options?: ScopedRequestOptions,
  ): Promise<T> => {
    if (!isContractCenterBffPath(url)) {
      throw new Error(`合同中心请求路径无效: ${url}`);
    }

    const response = await request<ContractCenterResponse<T>>(url, options);
    if (
      !response ||
      typeof response !== 'object' ||
      typeof response.code !== 'number' ||
      typeof response.msg !== 'string' ||
      !Object.hasOwn(response, 'data')
    ) {
      throw new Error('合同中心响应协议无效');
    }
    if (response.code !== 0) {
      throw new ContractCenterApiError(
        response.msg,
        response.code,
        response.data,
      );
    }
    return response.data as T;
  };
};
